"use client";

import { useRouter } from "@/lib/hooks/useRouter";
import Link from "next/link";
import { CSSProperties, JSX, MouseEventHandler, useEffect, useRef } from "react";
import styles from "./Error.module.css";

export function Error({
	header,
	description,
	link,
	infoText,
	height
}: {
	header: string,
	description: string | JSX.Element,
	link?: {
		text: string | JSX.Element
	} & ({
		href: string | (() => string)
	} | {
		onClick: MouseEventHandler<HTMLAnchorElement>
	}),
	infoText?: string,
	height?: CSSProperties["height"]
}) {
	const linkRef = useRef<HTMLAnchorElement>(null);

	useEffect(() => {
		if (!linkRef.current) return;
		if (link && "href" in link && link.href && typeof (link.href) !== "string") linkRef.current.href = link.href();
	}, [linkRef]);

	return (
		<div style={{ height }}>
			<div className={styles["error-container"]}>
				<p className={styles["error-massive-text"]}>Whoops!</p>
				<h1 className={styles["error-header"]}>{header}</h1>
				<p className={styles["error-description"]}>{description}</p>
				{link && ("href" in link ?
					(typeof (link.href) === "string" ?
						<Link className={styles["error-link"]} href={typeof (link.href) === "string" ? link.href : ""} scroll={false}>{link.text}</Link> :
						<a className={styles["error-link"]} href={typeof (link.href) === "string" ? link.href : undefined} ref={linkRef}>{link.text}</a>) :
					<a className={styles["error-link"]} onClick={"onClick" in link ? link.onClick : undefined}>{link.text}</a>
				)}
				{infoText && (
					<div className={styles["error-info-container"]}>
						<p></p>
						<p>{infoText}</p>
					</div>
				)}
			</div>
		</div>
	)
}

export function ErrorWithSignIn(props: Omit<Parameters<typeof Error>[0], "link">) {
	return (
		<Error
			link={{
				text: (<>
					<span>Sign in</span>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">{/*!Font Awesome Free v7.1.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.*/}<path d="M566.6 342.6C579.1 330.1 579.1 309.8 566.6 297.3L406.6 137.3C394.1 124.8 373.8 124.8 361.3 137.3C348.8 149.8 348.8 170.1 361.3 182.6L466.7 288L96 288C78.3 288 64 302.3 64 320C64 337.7 78.3 352 96 352L466.7 352L361.3 457.4C348.8 469.9 348.8 490.2 361.3 502.7C373.8 515.2 394.1 515.2 406.6 502.7L566.6 342.7z" /></svg>
				</>),
				href: () => `/login?redirect=${encodeURIComponent(window.location.pathname)}`
			}}
			{...props}
		/>
	)
}

export function ErrorWithBack(props: Omit<Parameters<typeof Error>[0], "link">) {
	const router = useRouter();

	return (
		<Error
			link={{
				text: (<>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">{/*!Font Awesome Free v7.0.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.*/}<path d="M73.4 297.4C60.9 309.9 60.9 330.2 73.4 342.7L233.4 502.7C245.9 515.2 266.2 515.2 278.7 502.7C291.2 490.2 291.2 469.9 278.7 457.4L173.3 352L544 352C561.7 352 576 337.7 576 320C576 302.3 561.7 288 544 288L173.3 288L278.7 182.6C291.2 170.1 291.2 149.8 278.7 137.3C266.2 124.8 245.9 124.8 233.4 137.3L73.4 297.3z" /></svg>
					<span>Back</span>
				</>),
				onClick: () => router.back()
			}}
			{...props}
		/>
	)
}